// The feature-request board's pure half: its vocabulary, how rows fall into columns, the
// order inside a column, which moves a card offers, and how a vote or a move lands in the
// list on screen. Board.jsx renders and sequences; what it decides about the rows is
// decided here, where node --test can reach it (the repo has no DOM harness).
//
// THE VOCABULARY IS A COPY. web/src never imports from src/, so these lists restate
// src/lib/feature-request-vocab.js, and test/web/feature-board.test.js loads that module
// and fails if the two differ. The server validates against its own copy; these only
// decide what is drawn.

import { areaLabels } from '../../layout/nav-items.js';
import { atLeastRole } from '../../session/roles.js';

/** The columns, in column order. */
export const STATUSES = Object.freeze(['new', 'planned', 'building', 'shipped', 'declined']);

export const STATUS_LABELS = Object.freeze({
  new: 'New',
  planned: 'Planned',
  building: 'Building',
  shipped: 'Shipped',
  declined: 'Declined',
});

/** How much it would help, least to most. */
export const IMPACTS = Object.freeze(['nice_to_have', 'would_help_a_lot', 'blocking_my_work']);

export const IMPACT_LABELS = Object.freeze({
  nice_to_have: 'Nice to have',
  would_help_a_lot: 'Would help a lot',
  blocking_my_work: 'Blocking my work',
});

/**
 * Where a request belongs: the nav's sections by label, in nav order, then the two places
 * that are not sections. nav-items.js `areaLabels` derives it from SECTIONS — one
 * derivation, which this only names — so a section added to the nav appears here, and then
 * fails the vocabulary test until the server's list has it too.
 */
export const AREAS = areaLabels();

/**
 * Within a column: most votes first, then newest. "Newest" is created_at (ISO strings
 * from the API, fixed width, so a string compare is a time compare), and on a tie the
 * higher id — as a BigInt, because '9' sorts after '10' as a string and an id may be past
 * Number precision.
 */
export function compareRequests(a, b) {
  if (a.vote_count !== b.vote_count) return b.vote_count - a.vote_count;
  if (a.created_at !== b.created_at) return a.created_at < b.created_at ? 1 : -1;
  const idA = BigInt(a.id);
  const idB = BigInt(b.id);
  if (idA === idB) return 0;
  return idA < idB ? 1 : -1;
}

/**
 * The five columns, always all five and always in order: an empty column is drawn with a
 * count of zero rather than disappearing, because on day one that is the whole board.
 * A row whose status the board does not know is drawn nowhere.
 *
 * @returns {Array<{status: string, label: string, count: number, requests: object[]}>}
 */
export function groupIntoColumns(requests) {
  return STATUSES.map((status) => {
    const inColumn = requests.filter((r) => r.status === status).sort(compareRequests);
    return { status, label: STATUS_LABELS[status], count: inColumn.length, requests: inColumn };
  });
}

/**
 * Whether to RENDER the move controls. The server's requireProductRole('support',
 * 'admin') on PATCH /:id/status is the authorization; this only declines to draw a
 * control it knows would 403.
 */
export function canMoveCards(user) {
  return atLeastRole(user, 'support', 'admin');
}

/**
 * The columns a card in `status` may be moved to by `user`: for a support admin, every
 * other column, backwards included (the server refuses no transition); for anyone else,
 * none.
 */
export function movesFor(status, user) {
  if (!canMoveCards(user)) return [];
  return STATUSES.filter((s) => s !== status);
}

/**
 * The list with one row's vote replaced by what POST /:id/vote answered. A row no longer
 * in the list is left absent.
 *
 * @param {{id: string, vote_count: number, voted_by_me: boolean}} vote
 */
export function applyVote(requests, vote) {
  return requests.map((r) => (
    r.id === vote.id ? { ...r, vote_count: vote.vote_count, voted_by_me: vote.voted_by_me } : r
  ));
}

/** The list with one row replaced by the server's copy of it (PATCH /:id/status answers it). */
export function replaceRequest(requests, updated) {
  return requests.map((r) => (r.id === updated.id ? updated : r));
}

/**
 * True when a failed vote or move may mean the list on screen is wrong rather than the
 * click: 404 (the request is gone) or 403 (the caller's rank or licence changed). The
 * board refetches instead of trusting what it holds. A 403 for a stale CSRF token is
 * refetched too: the GET is harmless, and telling the two apart is `failureMessage`'s job.
 */
export function shouldRefetch(err) {
  return err?.status === 404 || err?.status === 403;
}

/**
 * What a failed vote or move says, led by `what` ("Your vote did not count"). Only a 404
 * and a FORBIDDEN 403 (a rank refusal) get the board's own words; every other refusal —
 * a stale CSRF token is also a 403 — says what the server said.
 */
export function failureMessage(err, what) {
  if (err?.status === 404) {
    return `${what}: that request is no longer on the board. Reloading the board.`;
  }
  if (err?.status === 403 && err.code === 'FORBIDDEN') {
    return `${what}: you are not allowed to do that. Reloading the board.`;
  }
  return `${what}: ${err?.message || 'something went wrong.'}`;
}
